import { rem } from 'polished'
import React, { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import styled from 'styled-components'

import { bedbankOfferProductDetailView } from 'actions/AnalyticsActions'
import { fetchBedbankOfferById } from 'actions/BedbankOfferActions'
import { fetchTraderInformation, offerViewed } from 'actions/OfferActions'
import { accommodationOfferClicked } from 'analytics/eventDefinitions'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import HeadData from 'components/Common/HeadData'
import HttpStatus from 'components/Common/HttpStatus'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import Pane from 'components/Common/Pane'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import BedbankOfferContainer from 'components/OfferPage/Bedbank/BedbankOfferContainer'
import BedbankNavigableOfferContent from 'components/OfferPage/NavigableOfferContent/BedbankNavigableOfferContent'
import PropertyContainer from 'components/OfferPage/Property/PropertyContainer'
import { BottomMobileRelatedTravelItems } from 'components/OfferPage/RelatedTravelItems'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { urlHashDatePicker } from 'constants/url'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useProperty from 'hooks/Offers/useProperty'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import { useAppDispatch } from 'hooks/reduxHooks'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED } from 'lib/offer/highIntentOffers'
import offerPageURL from 'lib/offer/offerPageURL'

const MainContent = styled(Group)`
  flex-grow: 1;
`

const NavigableContentPane = styled(Pane)`
  padding-bottom: ${rem(24)};
`
const OfferPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ObservableWrapper = styled.div`
  padding-top: 1px;
`

interface Props extends RouteComponentProps<{ offerSlug: string, offerId: string }> {
  fetching: boolean
  offer?: App.BedbankOffer
  offerError?: any
  regionCode: string
  currency: string
  headlessMode: boolean
  traderInformation?: App.TraderInformation
  relatedTravelItems: App.OfferState['relatedTravelItems']
  lereLoadingError?: any
  propertyPageOfferId?: string
}

function BedbankOfferPage(props: Props) {
  const {
    fetching,
    match,
    offer,
    offerError,
    regionCode,
    currency,
    location,
    headlessMode,
    relatedTravelItems,
    traderInformation,
    propertyPageOfferId,
  } = props

  const dispatch = useAppDispatch()
  const [showNavOfferContent, setShowNavOfferContent] = useState(false)

  const offerId = propertyPageOfferId || match.params.offerId
  const dataLoaded = useRef(false)

  // Keep this last condition consistent with `fetchBedbankOfferAndBreadcrumbs.ts`.
  const shouldUsePropertyPage = config.ACCOMM_USE_LPP_PROPERTY_PAGE && !offer?.promotions.length
  const [propertyMapping, fetchingPropertyMapping] = usePropertyOfferMapping(OFFER_TYPE_BED_BANK, offer?.id, {
    disabled: !shouldUsePropertyPage,
  })
  // TODO(HP-1614): This is a workaround for the accommodation service mapping endpoint eagerly
  // returning a mapping, even though bedbank rates are disabled, meaning we might not be guaranteed
  // to successfully fetch property content.
  const hasValidPropertyMapping = !!propertyMapping?.bedbankPropertyIds?.includes(offer?.id ?? '')
  const [property] = useProperty(propertyMapping?.propertyId, { disabled: !shouldUsePropertyPage })

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  useEffect(() => {
    if (offer) {
      // we use dataLoaded ref to prevent multiple analytics pushes
      fireEcommerceGTMEvent(accommodationOfferClicked(offer, currency))
      dataLoaded.current = true
    }
  }, [offer, currency])

  usePageActivityTracking()

  useEffect(() => {
    if (!showNavOfferContent && location.hash && location.hash !== urlHashDatePicker) {
      setShowNavOfferContent(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash])

  useEffect(() => {
    if (showNavOfferContent) {
      window.location.href = location.hash
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNavOfferContent])

  useEffect(() => {
    if (offer && ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offer.type)) {
      dispatch(offerViewed(offer.id, offer.type, !offer.sell?.price))
    }
  }, [offer, dispatch])

  // This one is used to load the section before the user gets to it
  const [navigableRef, navigableInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  useEffect(() => {
    dispatch(fetchBedbankOfferById(offerId, { regionCode }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  useEffect(() => {
    if (isEnabledForFeature(config.TRADER_INFORMATION_REGIONS!, regionCode)) {
      dispatch(fetchTraderInformation(offerId, regionCode))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  useEffect(() => {
    if (offer) {
      dispatch(bedbankOfferProductDetailView(offer))
      window.dataLayer?.push({ offerType: offer.type })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  // ------------- LERE sticky footer -------------------
  const ymalStickyFooterShowTrigger = useInView({ triggerOnce: true })
  // ------------- END of LERE sticky footer -------------------

  if (offerError) {
    if (offerError.status === 404) {
      return <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>
    } else {
      return <HttpStatus code={500}>
        <Error500Page error={offerError} />
      </HttpStatus>
    }
  }

  if (fetchingPropertyMapping || (hasValidPropertyMapping && !property) || fetching || !offer) {
    return <LoadingIndicator data-testid="bedbank-offer-loading-indicator" />
  }

  if (match.params.offerSlug && (offer.slug !== match.params.offerSlug)) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  const metaDescription = `Book the best deal for ${offer.name}, ${offer.property.address.city} with Luxury Escapes today. The world's best escapes, handpicked by experts!`
  const breadCrumbItems = relatedTravelItems[offer?.id] || []

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    <HeadData
      title={`${offer.name}, ${offer.property.address.city} - ${config.title} ${regionCode}`}
      description={metaDescription}
      imageId={offer.images[0]?.id || undefined}
    />
    <OfferPageWrapper className="OfferPage">
      <MainContent direction="vertical" verticalAlign="space-between">
        <LuxPlusLabelProvider page="offer-page">
          {!property && <BedbankOfferContainer
            offerId={offerId}
            offer={offer}
            lereYmalStickyFooterShowTrigger={ymalStickyFooterShowTrigger}
          />}
          {property && <PropertyContainer
            property={property}
            offer={offer}
            lereYmalStickyFooterShowTrigger={ymalStickyFooterShowTrigger}
            shouldUseLereStickyFooter
          />}
        </LuxPlusLabelProvider>
        {!property && <SoftOfferPageWalledGarden
          bypassWall={isWalledGardenEuropeOffersEnabled}
          wall={<OfferPageSignUpWall offerType="hotel" />}
        />}
        <ObservableWrapper ref={navigableRef}>
          <NavigableContentPane>
            {(navigableInView || showNavOfferContent) && (
              <BedbankNavigableOfferContent
                offer={offer}
                traderInformation={traderInformation}
                hash={location.hash}
              />
            )}
          </NavigableContentPane>
        </ObservableWrapper>
        <ObservableWrapper>
          {!headlessMode && <BottomMobileRelatedTravelItems offer={offer} items={breadCrumbItems} title="Travel Destinations" />}
        </ObservableWrapper>
      </MainContent>
    </OfferPageWrapper>
  </AnalyticsPageContext.Provider>
}

const mapStateToProps = (state: App.State, ownProps: Partial<Props>) => {
  const offerId = ownProps.propertyPageOfferId || ownProps.match?.params?.offerId || ''
  const offer: App.BedbankOffer | undefined = state.offer.bedbankOffers[offerId]

  return {
    regionCode: state.geo.currentRegionCode,
    currency: state.geo.currentCurrency,
    fetching: !!state.offer.offersLoading[offerId] && !offer,
    offer,
    traderInformation: state.offer.traderInformation[offerId]?.data,
    offerError: state.offer.offerErrors[offerId],
    headlessMode: state.system.headlessMode,
    relatedTravelItems: state.offer.relatedTravelItems,
  }
}

export default withRouter(connect(mapStateToProps)(BedbankOfferPage))
